import { InjectedExperimentsProps, withExperiments } from '@wix/wix-experiments-react';
import * as React from 'react';
import { InjectedTranslateProps, translate } from 'react-i18next';
import { connect, ConnectedProps } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Link } from 'react-router-dom';
import Add from 'wix-ui-icons-common/Add';
import Settings from 'wix-ui-icons-common/Settings';
import ChevronDownSmall from 'wix-ui-icons-common/ChevronDownSmall';
import Edit from 'wix-ui-icons-common/Edit';
import { showModal } from '../../redux/actions/activeModal.actions';
import { getLoadingTeammates, getPermission, getTeamName } from '../../redux/selectors/teamDetails';
import { ModalName } from '../../types/ModalNames';
import { PageSubtitle } from '../TeamMembersListPage/PageSubtitle/PageSubtitle';
import TeamMembersListContainer from './TeamMembersListContainer';
import * as s from './TeamMembersListPage.scss';
import { RouteChildrenProps } from 'react-router';
import { getContext, getLocale, getIsSite } from '../../redux/selectors/config';
import { State } from '../../redux/configure-store';
import { getInvitePeoplePermission, isUserOwner } from '../../redux/selectors/teamDetails';
import { getManageRolesPermission } from '../../redux/selectors/teamDetails';
import TeamAndSiteOwnerHint from '../TeamAndSiteOwnerHint/TeamAndSiteOwnerHint';
import {
  reportManageRolesClick,
  reportOpenEditTeamProfileDialog,
  reportTeammatesPageLoad
} from '../../redux/actions/bi.actions';
import { PermissionKey } from '../../services/server-types';
import { getTransferOwnershipLink } from '../../redux/selectors/teamDetails';
import { Box, Button, Container, Row, Page, Tooltip, PopoverMenu, FloatingNotification } from 'wix-style-react';
import { getEnforceContributors } from '../../redux/actions/serverApi.actions';
import { EnforceContributorsButtonLoading } from '../EnforceContributors/EnforceContributors';
import * as css from '../../common/TeamMembersList/TeamMembersList.scss';

interface ITeamMembersListPage extends InjectedTranslateProps, RouteChildrenProps, InjectedExperimentsProps, ConnectedProps<typeof connector> {}

class TeamMembersListPage extends React.Component<ITeamMembersListPage> {
  state: { sentLoadEvent: boolean; inviteButtonLoading: boolean, notificationWidth: number };
  private readonly notificationRef: React.RefObject<HTMLInputElement>;

  constructor(props) {
    super(props);
    this.state = { sentLoadEvent: false, inviteButtonLoading: false, notificationWidth: 0 };
    this.notificationRef = React.createRef();
  }

  componentDidUpdate(): void {
    const { sentLoadEvent } = this.state;
    const { siteLevel, onTeamMembersListPageLoad, teamName, location } = this.props;
    if (!sentLoadEvent && (siteLevel || teamName)) {
      this.setState({ sentLoadEvent: true }, () => onTeamMembersListPageLoad((location as any)?.state?.origin ?? '/load'));
    }
  }

  componentDidMount(): void {
    window.addEventListener('resize', this.resize);
  }

  componentWillUnmount(): void {
    window.removeEventListener('resize', this.resize);
  }

  inviteContributorButtonAction = () => {
    const { invitePeopleButtonClick } = this.props;
    this.setState({ inviteButtonLoading: true });
    invitePeopleButtonClick().then(() => this.setState({ inviteButtonLoading: false }));
  }

  resize = () => {
    if (this.shouldDisplayNotification()) {
      this.setState({ notificationWidth: this.notificationRef?.current?.clientWidth });
    }
  }

  shouldDisplayNotification = () => {
    const { experiments, loadingTeammates } = this.props;
    const experimentLoadingTeammatesNotification = experiments.enabled('specs.ident.LoadingTeammatesNotification');
    return experimentLoadingTeammatesNotification && loadingTeammates;
  }

  render() {
    const { t, history, experiments, invitePeoplePermission, siteLevel, isOwner, locale, manageRolesPermission, updateProfilePermission, editTeamProfile, onManageRolesClick, transferOwnershipLink } = this.props;
    const { inviteButtonLoading } = this.state;
    const experimentCustomRoles = experiments.enabled('specs.wos.CustomRoles');
    const pageHeader = (siteLevel &&  <TeamAndSiteOwnerHint/>);
    const manageRolesButonProps = {
      disabled: !manageRolesPermission,
      prefixIcon: <Settings />
    };
    const pageTitle = t(siteLevel ? 'app.site.teamManagementTitle' : 'app.account.teamManagementTitle');
    const actionsBar = (<Box>
      <Box paddingRight={'small'}>
        {(
          updateProfilePermission &&
          !siteLevel ?
            (experimentCustomRoles ?
              (<PopoverMenu triggerElement={<Button skin="inverted" suffixIcon={<ChevronDownSmall />}>{t('app.moreActions')}</Button>}>
                  <PopoverMenu.MenuItem {...manageRolesButonProps} text={t('app.rolesCTA')} onClick={() => { onManageRolesClick(); return history.push('/roles', { origin: '/' }); }} />
                  <PopoverMenu.MenuItem prefixIcon={<Edit/>} text={t('app.editTeamProfile')} onClick={editTeamProfile} />
               </PopoverMenu>) :
               (<Button
                skin="inverted"
                disabled={!updateProfilePermission}
                onClick={editTeamProfile}
              >
                {t('app.editTeamProfile')}
              </Button >)) :
            (experimentCustomRoles && <Button
              skin="inverted"
              as={Link}
              to="/roles"
              {...manageRolesButonProps}
              onClick={onManageRolesClick}
            >
              {t('app.rolesCTA')}
            </Button >)
        )}
      </Box>
      <Box>
      <Tooltip
        maxWidth={258}
        appendTo="scrollParent"
        placement="left"
        textAlign="start"
        content={t('app.invitePeople.disabled.tooltip')}
        disabled={invitePeoplePermission}>
        <Button
          dataHook="invite-people-button"
          className={s.InvitePeopleButton}
          disabled={!invitePeoplePermission}
          onClick={this.inviteContributorButtonAction}
          prefixIcon={<Add style={{ visibility: inviteButtonLoading ?  'hidden' : 'initial' }}/>}
        >
          <EnforceContributorsButtonLoading text={siteLevel ? t('app.site.invitePeople') : t('app.account.invitePeople')} isLoading={inviteButtonLoading}/>
        </Button>
      </Tooltip>
      </Box>
    </Box>);

    return (
      <Page height="calc(100vh - 48px)">
        <Page.Header
          dataHook="account-team-header"
          className={s.pageHeader}
          title={pageTitle}
          subtitle={<PageSubtitle isOwner={isOwner} locale={locale} siteLevel={siteLevel}/>}
          actionsBar={actionsBar}
        />

        <Page.Content>
          <Container>
            <div className={css.notificationWrapper} ref={this.notificationRef}>
              { this.shouldDisplayNotification() && <div className={css.notificationContainer} style={ { width: `${this.notificationRef?.current?.clientWidth}px` } }>
                <FloatingNotification text={t('list.loading.users')} className={css.notification} showCloseButton={false} />
              </div> }
            </div>
            <Row>
              <TeamMembersListContainer header={pageHeader} siteLevel={siteLevel} transferOwnershipLink={transferOwnershipLink}/>
            </Row>
          </Container>
        </Page.Content>
      </Page>
    );
  }
}

const mapStateToProps = (state: State) => ({
  teamName: getTeamName(state),
  isOwner: isUserOwner(state),
  invitePeoplePermission: getInvitePeoplePermission(state),
  manageRolesPermission: getManageRolesPermission(state),
  updateProfilePermission: getPermission(state, PermissionKey.UPDATE_PROFILE),
  siteLevel: getIsSite(state),
  context: getContext(state),
  locale: getLocale(state),
  debug: state.debug,
  transferOwnershipLink: getTransferOwnershipLink(state),
  loadingTeammates: getLoadingTeammates(state),
});

const mapDispatchToProps = (dispatch) => ({
  editTeamProfile: () => {
    dispatch(reportOpenEditTeamProfileDialog());
    return dispatch(showModal(ModalName.EditTeamDetails));
  },
  onTeamMembersListPageLoad: (path) => dispatch(reportTeammatesPageLoad(path)),
  onManageRolesClick: () => dispatch(reportManageRolesClick('/')),
  invitePeopleButtonClick: () => dispatch(getEnforceContributors( { origin: '/' })),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

export default withRouter(connector(translate()(withExperiments(TeamMembersListPage))));
